import { CompensationReviewCampaignStatus, CompensationReviewRecommendationStatus, UserRole } from "@prisma/client";
import { type AppContext } from "~/lib/context";
import {
  formatCompensationReviewCampaign,
  formatCompensationReviewReviewer,
  formatKnockTenant,
  formatKnockUser,
  selectCompensationReviewReviewerForFormat,
  selectUserForFormat,
} from "~/lib/external/knock/format";
import { track } from "~/lib/external/segment/server/client";
import { uniqBy } from "~/lib/lodash";
import { type EventPayload } from "~/workers/handleEvent";

export const notifyCampaignAdmins = async (
  ctx: AppContext,
  params: EventPayload<"CompensationReview_RecommendationSubmitted">
) => {
  if (!ctx.featureFlags.CAN_ACCESS_KNOCK_NOTIFICATIONS) return;

  const campaign = await ctx.prisma.compensationReviewCampaign.findUniqueOrThrow({
    where: { id: params.campaignId },
  });

  if (campaign.status !== CompensationReviewCampaignStatus.IN_PROGRESS) return;

  const recommendation = await ctx.prisma.compensationReviewRecommendation.findUniqueOrThrow({
    where: {
      campaignId: campaign.id,
      id: params.recommendationId,
    },
    select: {
      reviewer: {
        select: selectCompensationReviewReviewerForFormat,
      },
    },
  });

  if (!recommendation.reviewer) return;

  const nonSubmittedRecommendationsCount = await ctx.prisma.compensationReviewRecommendation.count({
    where: {
      reviewerId: recommendation.reviewer.id,
      OR: [{ submittedAt: null }, { status: CompensationReviewRecommendationStatus.LOCKED }],
    },
  });

  if (nonSubmittedRecommendationsCount > 0) return;

  const campaignAdmins = await ctx.prisma.compensationReviewAdmin.findMany({
    where: { campaignId: campaign.id },
    select: {
      user: { select: selectUserForFormat },
    },
  });
  const figuresAdmins = await ctx.prisma.user.findMany({
    where: { companyId: campaign.companyId, permissions: { role: UserRole.ADMIN }, blockedAt: null },
    select: selectUserForFormat,
  });
  const admins = uniqBy([...campaignAdmins.map((admin) => admin.user), ...figuresAdmins], (user) => user.id);

  await track(ctx, "Compensation Review Recommendations Submitted", {
    onBehalfOfUser: params.onBehalfOfUser,
    company: formatKnockTenant(params.onBehalfOfUser.company),
    campaign: formatCompensationReviewCampaign(campaign),
    reviewer: formatCompensationReviewReviewer(recommendation.reviewer),
    admins: admins.map((admin) => formatKnockUser(admin)),
  });
};
